import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ComplianceSection from './components/ComplianceSection';
import DataRetentionTimeline from './components/DataRetentionTimeline';
import AdvocallConnection from './components/CyfireConnection';
import ConsultationRecording from './components/ConsultationRecording';
import PrivacySettings from './components/PrivacySettings';


const LegalCompliance = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const quickLinks = [
    { name: 'Datenschutzerklärung', icon: 'Shield', href: '#privacy' },
    { name: 'AGB', icon: 'FileText', href: '#terms' },
    { name: 'Cookie-Richtlinie', icon: 'Cookie', href: '#cookies' }
  ];

  const complianceStats = [
    { label: 'DSGVO-konform', value: '100%', icon: 'Shield' },
    { label: 'Zertifizierte Anwälte', value: '10+', icon: 'UserCheck' },
    { label: 'Jahre Rechtserfahrung', value: '8+', icon: 'Award' },
    { label: 'Beratungen durchgeführt', value: '5.000+', icon: 'Phone' }
  ];

  return (
    <>
      <Helmet>
        <title>Rechtliche Compliance - AdvoCall Professional</title>
        <meta name="description" content="Umfassende rechtliche Dokumentation, Datenschutz und professionelle Standards. DSGVO-konforme Beratung durch zertifizierte Rechtsanwälte." />
        <meta name="keywords" content="Rechtliche Compliance, Datenschutz, DSGVO, Rechtsanwalt, Beratung, Frankfurt" />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <Header />

        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-primary to-accent text-white py-16">
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mx-auto mb-6">
                <Icon name="Scale" size={32} color="white" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                Rechtliche Compliance
              </h1>
              <p className="text-xl text-white/90 max-w-3xl mx-auto mb-8">
                Transparenz, Datenschutz und professionelle Standards für vertrauensvolle Rechtsberatung
              </p>
              
              {/* Quick Links */}
              <div className="flex flex-wrap justify-center gap-4">
                {quickLinks?.map((link, index) => (
                  <a
                    key={index}
                    href={link?.href}
                    className="flex items-center space-x-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-all duration-200 text-white"
                  >
                    <Icon name={link?.icon} size={16} />
                    <span className="text-sm font-medium">{link?.name}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {complianceStats?.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg mx-auto mb-3">
                    <Icon name={stat?.icon} size={20} color="var(--color-primary)" />
                  </div>
                  <div className="text-2xl font-bold text-primary mb-1">{stat?.value}</div>
                  <div className="text-sm text-text-secondary">{stat?.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-12">
              
              {/* Advocall Connection */}
              <AdvocallConnection />

              {/* Privacy Policy Section */}
              <ComplianceSection 
                title="Datenschutzerklärung" 
                icon="Shield"
                id="privacy"
              >
                <div className="space-y-4">
                  <p>
                    AdvoCall Professional, betrieben durch die ADVOCALL RECHTSANWALTSGESELLSCHAFT MBH,
                    verpflichtet sich zu höchsten Datenschutzstandards gemäß der Datenschutz-Grundverordnung (DSGVO)
                    und dem Bundesdatenschutzgesetz (BDSG).
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                    <div className="space-y-3">
                      <h4 className="font-semibold text-text-primary">Datenverarbeitung</h4>
                      <ul className="space-y-2 text-sm">
                        <li className="flex items-start space-x-2">
                          <Icon name="Check" size={16} color="var(--color-success)" className="mt-0.5" />
                          <span>Nur erforderliche Daten für Beratungsleistungen</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <Icon name="Check" size={16} color="var(--color-success)" className="mt-0.5" />
                          <span>Verschlüsselte Übertragung und Speicherung</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <Icon name="Check" size={16} color="var(--color-success)" className="mt-0.5" />
                          <span>Keine Weitergabe an Dritte ohne Einwilligung</span>
                        </li>
                      </ul>
                    </div>
                    <div className="space-y-3">
                      <h4 className="font-semibold text-text-primary">Ihre Rechte</h4>
                      <ul className="space-y-2 text-sm">
                        <li className="flex items-start space-x-2">
                          <Icon name="Check" size={16} color="var(--color-success)" className="mt-0.5" />
                          <span>Auskunft über gespeicherte Daten</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <Icon name="Check" size={16} color="var(--color-success)" className="mt-0.5" />
                          <span>Berichtigung unrichtiger Daten</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <Icon name="Check" size={16} color="var(--color-success)" className="mt-0.5" />
                          <span>Löschung oder Einschränkung der Verarbeitung</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </ComplianceSection>

              {/* Privacy Settings Dashboard */}
              <PrivacySettings />

              {/* Terms of Service */}
              <ComplianceSection 
                title="Allgemeine Geschäftsbedingungen" 
                icon="FileText"
                id="terms"
              >
                <div className="space-y-4">
                  <p>
                    Die Nutzung der AdvoCall Professional Beratungsleistungen unterliegt den folgenden 
                    Geschäftsbedingungen, die im Einklang mit deutschem Recht und der Berufsordnung 
                    für Rechtsanwälte (BRAO) stehen.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                    <div className="bg-muted/50 p-4 rounded-lg">
                      <h4 className="font-semibold text-text-primary mb-2">Beratungsumfang</h4>
                      <p className="text-sm text-text-secondary">
                        Telefonische Erstberatung zu rechtlichen Fragestellungen. 
                        Keine Vertretung vor Gericht oder umfassende Mandatsübernahme.
                      </p>
                    </div>
                    <div className="bg-muted/50 p-4 rounded-lg">
                      <h4 className="font-semibold text-text-primary mb-2">Preisstruktur</h4>
                      <p className="text-sm text-text-secondary">
                        Transparente Minutenabrechnung: 2,99 EUR/Minute. 
                        Keine versteckten Kosten oder Mindestgesprächsdauer.
                      </p>
                    </div>
                    <div className="bg-muted/50 p-4 rounded-lg">
                      <h4 className="font-semibold text-text-primary mb-2">Haftung</h4>
                      <p className="text-sm text-text-secondary">
                        Vollständige Berufshaftpflichtversicherung. 
                        Haftung nach gesetzlichen Bestimmungen der BRAO.
                      </p>
                    </div>
                  </div>
                </div>
              </ComplianceSection>

              {/* Consultation Recording Policies */}
              <ConsultationRecording />

              {/* Data Retention Timeline */}
              <DataRetentionTimeline />

              {/* Professional Standards */}
              <ComplianceSection 
                title="Professionelle Standards" 
                icon="Award"
              >
                <div className="space-y-4">
                  <p>
                    Alle AdvoCall Professional Berater sind zugelassene Rechtsanwälte und unterliegen 
                    den strengen Berufsregeln der deutschen Rechtsanwaltschaft.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                    <div>
                      <h4 className="font-semibold text-text-primary mb-3">Qualifikationen</h4>
                      <ul className="space-y-2 text-sm text-text-secondary">
                        <li>• Abgeschlossenes Jurastudium (1. und 2. Staatsexamen)</li>
                        <li>• Zulassung bei der Rechtsanwaltskammer</li>
                        <li>• Regelmäßige Fortbildungen und Spezialisierungen</li>
                        <li>• Mindestens 3 Jahre Berufserfahrung</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-text-primary mb-3">Ethische Grundsätze</h4>
                      <ul className="space-y-2 text-sm text-text-secondary">
                        <li>• Absolute Verschwiegenheitspflicht</li>
                        <li>• Interessenkonflikte werden vermieden</li>
                        <li>• Unabhängige und objektive Beratung</li>
                        <li>• Transparente Kommunikation über Grenzen der Beratung</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </ComplianceSection>

              {/* Cookie Policy */}
              <ComplianceSection 
                title="Cookie-Richtlinie" 
                icon="Cookie"
                id="cookies"
              >
                <div className="space-y-4">
                  <p>
                    AdvoCall Professional verwendet Cookies zur Verbesserung der Nutzererfahrung 
                    und zur Bereitstellung personalisierter Inhalte.
                  </p>
                  <div className="bg-muted/50 p-4 rounded-lg">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <h5 className="font-medium text-text-primary mb-2">Notwendige Cookies</h5>
                        <p className="text-text-secondary">
                          Für grundlegende Website-Funktionen erforderlich. 
                          Können nicht deaktiviert werden.
                        </p>
                      </div>
                      <div>
                        <h5 className="font-medium text-text-primary mb-2">Funktionale Cookies</h5>
                        <p className="text-text-secondary">
                          Speichern Ihre Präferenzen und Einstellungen 
                          für eine personalisierte Erfahrung.
                        </p>
                      </div>
                      <div>
                        <h5 className="font-medium text-text-primary mb-2">Analyse Cookies</h5>
                        <p className="text-text-secondary">
                          Helfen uns zu verstehen, wie Sie unsere 
                          Website nutzen und diese zu verbessern.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </ComplianceSection>

              {/* Contact for Legal Questions */}
              <div className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-lg p-6 border border-primary/20">
                <div className="text-center">
                  <Icon name="MessageCircle" size={32} color="var(--color-primary)" className="mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-text-primary mb-2">
                    Fragen zu unseren rechtlichen Bestimmungen?
                  </h3>
                  <p className="text-text-secondary mb-6">
                    Unser Datenschutzbeauftragter und unser Compliance-Team stehen Ihnen gerne zur Verfügung.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button variant="default" iconName="Mail" iconPosition="left">
                      datenschutz@advocall.de
                    </Button>
                    <Button variant="outline" iconName="Phone" iconPosition="left">
                      +49 69 1234 5679
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-slate-900 text-white py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="flex items-center justify-center space-x-3 mb-4">
                <img
                  src="/assets/images/Logo-Blue-and-white-use-on-black-1788636880205.png"
                  alt="AdvoCall Rechtsanwaltsgesellschaft Logo"
                  className="h-12 w-auto object-contain"
                />
              </div>
              <p className="text-slate-400 mb-6">
                Rechtliche Compliance und Transparenz für vertrauensvolle Beratung
              </p>
              <div className="flex flex-wrap justify-center gap-6 text-sm text-slate-400">
                <span>© {new Date()?.getFullYear()} ADVOCALL RECHTSANWALTSGESELLSCHAFT MBH</span>
                <span>•</span>
                <span>Alle Rechte vorbehalten</span>
                <span>•</span>
                <span>DSGVO-konform</span>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default LegalCompliance;